import React, { useState, useEffect, useRef } from "react";
import { GoBell } from "react-icons/go";
import { motion, AnimatePresence } from "framer-motion";
import { LanguageSwitcher } from "../LanguageSwitcher/LanguageSwitcher";
import { Notification } from "../../types/notificationTypes"; // Adjust path as needed
import { useTranslation } from "react-i18next";

type Props = {
  notifications: Notification[];
  loading: boolean;
};

const RestaurantHeader = ({ notifications, loading }: Props) => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [page, setPage] = useState(1);
  const [notificationsToShow, setNotificationsToShow] = useState<
    Notification[]
  >([]);
  const {t} = useTranslation();

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const notificationsPerPage = 10;

  useEffect(() => {
    const startIdx = (page - 1) * notificationsPerPage;
    const endIdx = page * notificationsPerPage;
    setNotificationsToShow(notifications.slice(0, endIdx));
  }, [notifications, page]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNotificationClick = (id: number) => {
    setSelectedNotification(
      notifications.find((note) => note.id === id) || null
    );
  };

  const handleLoadMore = () => {
    setPage(page + 1);
  };

  const unreadCount = notifications.filter((note) => !note.read).length;

  return (
    <div className="flex gap-3 pr-12">
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={() => setIsNotificationOpen(!isNotificationOpen)}
          className="border w-[40px] h-[40px] flex justify-center items-center rounded-lg border-gray-200 hover:bg-gray-50 relative"
        >
          <GoBell className="text-xl" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center w-2 h-2 bg-red-600 rounded-full transform translate-x-1/2 -translate-y-1/2"></span>
          )}
        </button>

        {isNotificationOpen && (
          <div className="absolute right-0 z-50 w-72 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-80 overflow-auto">
            {loading ? (
              <div className="p-4 text-sm text-gray-500">
                {t('notification.loading')}
              </div>
            ) : notificationsToShow.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">{t('notification.noNotifications')}</div>
            ) : (
              notificationsToShow.map((note: any) => (
                <div
                  key={note.id}
                  className={`p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                    note.read ? "bg-gray-100" : ""
                  }`}
                  onClick={() => handleNotificationClick(note.id)}
                >
                  <p className="font-medium text-gray-800">{note.title}</p>
                  <p className="text-sm text-gray-600 truncate">
                    {note.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(note.createdAt).toLocaleString()}
                  </p>
                </div>
              ))
            )}
            {!loading && notificationsToShow.length < notifications.length && (
              <button
                onClick={handleLoadMore}
                className="w-full py-2 text-sm text-center text-blue-500 hover:bg-blue-50"
              >
                {t("notification.loadMore")}
              </button>
            )}

            {!loading &&
              notificationsToShow.length === notifications.length && (
                <div className="p-3 text-center text-sm text-gray-500">
                  {t("notification.noMore")}
                </div>
              )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedNotification && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedNotification(null)}
          >
            <motion.div
              className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full relative"
              initial={{ scale: 0.8, rotateX: -90, opacity: 0 }}
              animate={{
                scale: 1,
                rotateX: 0,
                opacity: 1,
                transition: { type: "spring", stiffness: 150 },
              }}
              exit={{ scale: 0.8, rotateX: -90, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {selectedNotification.title}
              </h2>
              <p className="text-gray-700 mb-4">
                {selectedNotification.message}
              </p>
              <p className="text-xs text-gray-400">
                {new Date(selectedNotification.createdAt).toLocaleString()}
              </p>
              <button
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                onClick={() => setSelectedNotification(null)}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <LanguageSwitcher />
    </div>
  );
};

export default RestaurantHeader;
